import { prettyMilliseconds } from "@-/com.npmjs.pretty-ms";
import {
  addDays,
  format,
  startOfDay,
  startOfMonth,
  startOfWeek,
  startOfYear,
} from "@-/com.npmjs.date-fns";

export type StudyInterval = { endedAt: string; startedAt: string };
/** Selectable Stats Study Time windows (Day was dropped: Month already includes today). */
export type StudyTimePeriod = "week" | "month" | "year" | "all";
/** Which weekday a calendar week starts on (the actor's `weekStartsOn` setting). */
export type WeekStart = "sunday" | "monday";

/** `weekStartsOn` as date-fns' numeric day index: Sunday is 0, Monday is 1. */
export function weekStartsOnIndex(weekStart: WeekStart): 0 | 1 {
  return weekStart === "sunday" ? 0 : 1;
}

export function studyPeriodStart(
  date: Date,
  period: Exclude<StudyTimePeriod, "all">,
  weekStart: WeekStart = "monday",
): Date {
  if (period === "week") {
    return startOfWeek(date, { weekStartsOn: weekStartsOnIndex(weekStart) });
  }
  if (period === "month") {
    return startOfMonth(date);
  }
  return startOfYear(date);
}

export function computeStudyTime(
  sessions: readonly StudyInterval[],
  now = new Date(),
  weekStart: WeekStart = "monday",
) {
  // Union overlapping intervals (e.g. simultaneous devices/tabs) instead of double counting.
  const intervals = sessions
    .map((s) => ({
      end: Math.min(Date.parse(s.endedAt), now.getTime()),
      start: Date.parse(s.startedAt),
    }))
    .filter((s) => Number.isFinite(s.start) && Number.isFinite(s.end) && s.end > s.start)
    .sort((a, b) => a.start - b.start);
  const merged: { end: number; start: number }[] = [];
  for (const interval of intervals) {
    const previous = merged.at(-1);
    if (previous && previous.end >= interval.start) {
      previous.end = Math.max(previous.end, interval.end);
    } else {
      merged.push({ ...interval });
    }
  }
  const daily = new Map<string, number>();
  let totalMs = 0;
  for (const interval of merged) {
    totalMs += interval.end - interval.start;
    let cursor = interval.start;
    while (cursor < interval.end) {
      const date = new Date(cursor);
      const end = Math.min(interval.end, addDays(startOfDay(date), 1).getTime());
      const key = format(date, "yyyy-MM-dd");
      daily.set(key, (daily.get(key) ?? 0) + end - cursor);
      cursor = end;
    }
  }
  const since = (startMs: number) =>
    merged.reduce((sum, row) => sum + Math.max(0, row.end - Math.max(startMs, row.start)), 0);
  return {
    daily: Array.from(daily, ([day, durationMs]) => ({ day, durationMs })),
    monthMs: since(studyPeriodStart(now, "month", weekStart).getTime()),
    todayMs: since(startOfDay(now).getTime()),
    totalMs,
    weekMs: since(studyPeriodStart(now, "week", weekStart).getTime()),
    yearMs: since(studyPeriodStart(now, "year", weekStart).getTime()),
  };
}

export function formatStudyDuration(ms: number): string {
  return prettyMilliseconds(Math.max(0, ms), {
    compact: ms < 3_600_000,
    hideSeconds: ms >= 60_000,
    secondsDecimalDigits: 0,
    unitCount: 2,
  });
}
